import * as React from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { Box, Button, List, useToggle } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS_FACTORIAL_TIPS } from 'config/Vars'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import { apiClient } from 'modules/core/lib/mobx-rest'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import users from 'modules/core/store/collections/users'
import app from 'modules/core/store/models/app'

import contractVersions from 'modules/contracts/store/collections/contractVersions'
import {
  default as employees,
  default as employeesCollection,
} from 'modules/employees/store/collections/employees'
import ConfirmationModal from 'modules/job_catalog/components/ConfirmationModal'
import RolesTable from 'modules/job_catalog/components/RolesTable'
import useEmployeesWithWrongJobLevel from 'modules/job_catalog/hooks/useEmployeesWithWrongJob'
import useEmployeesWithoutJobLevel from 'modules/job_catalog/hooks/useEmployeesWithoutJobLevel'
import jobLevelsCollection, { jobRoleLevels } from 'modules/job_catalog/store/collections/levels'
import jobRolesCollection, { employeeJobRole } from 'modules/job_catalog/store/collections/roles'
import jobRolesAnalyticsCollection from 'modules/job_catalog/store/collections/rolesAnalytics'
import type JobLevel from 'modules/job_catalog/store/models/JobLevel'
import JobRole from 'modules/job_catalog/store/models/JobRole'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import CompetenciesDiscoveryModal from '../../components/CompetenciesDiscoveryModal'

interface LocationState {
  file: File | null
}

const Component = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const locationState = useLocation().state as LocationState | null
  const api = useMemo(() => apiClient(), [])
  const [nextRemovedRole, setNextRemovedRole] = React.useState<JobRole>()
  const [removeRole, setRemoveRole] = React.useState<boolean>()
  const [description, setDescription] = React.useState<string>(
    i18n.t('job_catalog.remove_role_confirmation_description')
  )
  const [hideDiscoveryModal, setHideDiscoveryModal] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS_FACTORIAL_TIPS.hideCompetenciesDiscoveryModalTip
  )
  const [canOpenModal, toggle] = useToggle(!hideDiscoveryModal)
  const canCreateJob = policyAuthorizations.for('job_catalog.create_job')
  const canManageCompetencies = users.current.policy.can('competencies.competenciesManage')
  const isRoleEditor = jobRolesCollection.some((x) =>
    policyAuthorizations.for('job_catalog.edit_job', x)
  )
  const hasViewModalPermissions = canCreateJob || isRoleEditor || canManageCompetencies
  const showModal = canOpenModal && hasViewModalPermissions
  const canSeeAggregatedInfo = users.current.policy.can(
    'employees.employeeSeeAggregatedPersonalInfo'
  )
  const employeesToShow = employeesCollection.active.filter((x) =>
    policyAuthorizations.for('contracts.assign_jobs', x)
  )
  const { employees: employeesWithWrongJobLevel } = useEmployeesWithWrongJobLevel(
    employeesToShow,
    contractVersions
  )
  const { employees: employeesWithoutJobLevel } = useEmployeesWithoutJobLevel(
    employeesToShow,
    contractVersions
  )

  useAsyncData(
    () =>
      Promise.all([
        policyAuthorizations.authorizePolicies('job_catalog.create_job'),
        policyAuthorizations.authorizePolicies('job_catalog.edit_job'),
        policyAuthorizations.authorizePolicies(
          'contracts.assign_jobs',
          employeesCollection.toArray()
        ),
      ]),
    []
  )

  const {
    createNewJobStart,
    goToEmployeeWithoutJobTitleSubmit,
    archiveJobRoleSubmit,
    archiveJobRoleCancel,
  } = trackingEvents.jobCatalog

  const editJobsOptions: boolean =
    !!employeesWithoutJobLevel?.length || !!employeesWithWrongJobLevel?.length

  const handleClick = () => {
    track(createNewJobStart)
    navigate(navigation.employees.jobCatalog.list.add.fullPath())
  }

  const { performAction, running, success, error, value } = useAsyncAction(
    () => {
      const file = locationState?.file
      return api.post(JobRole.importUrl(), { file })
    },
    {
      successFlash: false,
    }
  )

  const { loading: loadingAnalytics } = useAsyncData(() => jobRolesAnalyticsCollection.fetch(), [])

  useEffect(() => {
    track(trackingEvents.employee.page.jobCatalog.visited)
  }, [])
  useUnsafeEffect(() => {
    if (!!locationState?.file) performAction()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationState])

  useUnsafeEffect(() => {
    if (!!value?.validations.length) {
      navigate(navigation.employees.jobCatalog.import_errors.fullPath(), {
        state: { validations: value.validations },
      })
    }
    if (success && !!value?.roles.length) {
      jobRolesCollection.add(value.roles)
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success, error, value])

  const onRemoveRole = (role: JobRole) => {
    setNextRemovedRole(role)
    const employeesWithRole = employees.filter(
      (x) => employeeJobRole(contractVersions, x)?.attributes.id === role.attributes.id
    )
    if (employeesWithRole.length) {
      setDescription(
        i18n.t('job_catalog.reassign_role_confirmation_description', {
          employees: employeesWithRole.length,
        })
      )
    }
  }

  const handleRemoveRole = async (role: JobRole) => {
    setRemoveRole(true)

    const levels = jobRoleLevels(role).reduce<Record<string, JobLevel[]>>((acc, level) => {
      const hasLevelToArchive = contractVersions.some(
        (x) => x.jobCatalogLevelId === level.attributes.id
      )
      if (hasLevelToArchive) {
        if (!acc['levelsToArchive']) {
          acc['levelsToArchive'] = [level]
        } else {
          !acc['levelsToArchive'].includes(level)
            ? (acc['levelsToArchive'] = [...acc.levelsToArchive, level])
            : (acc['levelsToArchive'] = [...acc.levelsToArchive])
        }
      } else {
        if (!acc['leveslToDestroy']) {
          acc['leveslToDestroy'] = [level]
        } else {
          !acc['leveslToDestroy'].includes(level)
            ? (acc['leveslToDestroy'] = [...acc.leveslToDestroy, level])
            : (acc['leveslToDestroy'] = [...acc.leveslToDestroy])
        }
      }

      return acc
    }, {})

    const { success, value } = await asyncAction(() => {
      if (levels.levelsToArchive?.length) {
        return Promise.all([
          levels.levelsToArchive.map((x) => x.rpc('archive')),
          role.rpc('archive'),
          levels.leveslToDestroy?.map((x) => x.delete()),
        ])
      }
      return role.delete()
    })
    if (success) {
      await jobRolesCollection.fetch()
      await jobLevelsCollection.fetch()
      setNextRemovedRole(undefined)
      role.set(value)
    }
    setRemoveRole(false)
    track(archiveJobRoleSubmit, {
      is_successful: success,
      error_code: success ? '' : JSON.stringify(value),
    })
  }

  return (
    <ResponsivePage.Body
      widget={
        editJobsOptions ? (
          <List>
            {!!employeesWithoutJobLevel?.length && (
              <List.Item>
                <List.Title>
                  {employeesWithoutJobLevel.length} {i18n.t('job_catalog.no_employee_job_title')}
                </List.Title>
                <Button.Router
                  aria-label="bulk-assign-jobs"
                  onClick={() => track(goToEmployeeWithoutJobTitleSubmit)}
                  to={`${navigation.employees.jobCatalog.bulkEdit.fullPath()}?state=unassigned`}
                  type="secondary"
                  iconName="settings"
                />
              </List.Item>
            )}
            {!!employeesWithWrongJobLevel?.length && (
              <List.Item>
                <List.Title>
                  {employeesWithWrongJobLevel?.length} {i18n.t('job_catalog.wrong_job_title')}
                </List.Title>
                <Button.Router
                  to={`${navigation.employees.jobCatalog.bulkEdit.fullPath()}?state=wrong-job-data`}
                  type="secondary"
                  iconName="settings"
                />
              </List.Item>
            )}
          </List>
        ) : null
      }
    >
      <>
        {canCreateJob && (
          <Box
            paddingBottom="s24"
            flexDirection="row"
            gap="s8"
            alignItems="center"
            justifyContent="flexEnd"
          >
            <Button
              onClick={handleClick}
              iconName="add"
              extra={{ 'data-intercom-target': 'job-catalog-new-job-button' }}
            >
              {i18n.t('job_catalog.new_job')}
            </Button>
            <Button.Router
              type="secondary"
              to={navigation.employees.jobCatalog.list.import.fullPath()}
            >
              {i18n.t('job_catalog.import_jobs_title')}
            </Button.Router>
          </Box>
        )}
        <RolesTable
          loading={running}
          loadingAnalytics={loadingAnalytics}
          canSeeAggregatedInfo={canSeeAggregatedInfo}
          handleRemove={(role) => onRemoveRole(role)}
        />

        {nextRemovedRole && (
          <ConfirmationModal
            illustration="delete"
            title={i18n.t('job_catalog.remove_role_confirmation_title')}
            description={description}
            confirmText={i18n.t('common.remove')}
            disableConfirm={removeRole}
            onConfirm={() => handleRemoveRole(nextRemovedRole)}
            onCancel={() => {
              setNextRemovedRole(undefined)
              track(archiveJobRoleCancel)
            }}
          />
        )}
      </>
      <Outlet />
      {showModal && (
        <CompetenciesDiscoveryModal
          onClose={() => {
            setHideDiscoveryModal(true)
            toggle(false)()
          }}
          handleOnComplete={() => setHideDiscoveryModal(true)}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default Component
